// The ONE contract call the commons chip-in makes: ERC-20 transfer(address,uint256).
// Hand-rolled (no viem/wagmi) — selector + two 32-byte words — so the universal
// address+QR path needs zero JS and the one-click path adds only this file.

export type Eip1193 = { request(args: { method: string; params?: unknown[] }): Promise<unknown> };

const TRANSFER_SELECTOR = "a9059cbb"; // keccak256("transfer(address,uint256)")[:4]

function pad32(hex: string): string {
  return hex.toLowerCase().padStart(64, "0");
}

/** ABI-encode transfer(to, amount). `to` is a 0x address; `amount` is base units. */
export function encodeTransfer(to: string, amount: bigint): string {
  const addr = to.replace(/^0x/i, "");
  if (!/^[0-9a-fA-F]{40}$/.test(addr)) throw new Error("bad address");
  if (amount < 0n) throw new Error("negative amount");
  return "0x" + TRANSFER_SELECTOR + pad32(addr) + pad32(amount.toString(16));
}

/** Scale a human decimal string (e.g. "5.50") to integer base units. Rejects
 *  more fractional digits than the token supports. */
export function toBaseUnits(decimalAmount: string, decimals: number): bigint {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(decimalAmount.trim());
  if (!m) throw new Error("bad amount");
  const whole = m[1];
  const frac = m[2] ?? "";
  if (frac.length > decimals) throw new Error(`too many decimals (max ${decimals})`);
  const padded = (frac + "0".repeat(decimals)).slice(0, decimals);
  return BigInt(whole) * 10n ** BigInt(decimals) + BigInt(padded || "0");
}

export function hasInjectedProvider(): boolean {
  return typeof window !== "undefined" && !!(window as unknown as { ethereum?: unknown }).ethereum;
}

export async function sendErc20Transfer(
  provider: Eip1193, token: string, to: string, amount: bigint, from: string,
): Promise<string> {
  const data = encodeTransfer(to, amount);
  const hash = await provider.request({ method: "eth_sendTransaction", params: [{ from, to: token, data }] });
  return String(hash);
}
